import { useCallback, useEffect, useRef, useState } from "react";
import * as ml from "../../api/ml";
import { getProject } from "../../api/projects";
import type { Project } from "../../api/types";
import { IMAGE_SIZES, MIN_REVIEWED, MODEL_SIZES, configLine, runSummary, splitAdvice, splitLabel, versionLabel } from "../../lib/training";
import { isSetUp, setupLine, trainingOptions } from "../../lib/projectSetup";
import { summarizeResults } from "../../lib/results";
import { ProjectSetupDialog } from "../../dialogs/ProjectSetupDialog";
import { projectDataChanged, projectsChanged } from "../../state/currentProject";
import { useSession } from "../../state/session";
import { navigate } from "../../router";
import { useModelActions } from "../../ui/useModelActions";
import { JobLog } from "../JobParts";
import { SettingsGuide, SplitGuide } from "./TrainGuide";
import { ValidationResults } from "./ValidationResults";
import { AugmentPanel } from "./AugmentPanel";
import { DEFAULT_AUGMENT } from "../../lib/augment";
import { useTrainingJob } from "./useTrainingJob";
import { augmentAdvice, type AugmentAdvice as Advice } from "../../lib/augmentAdvice";
import { backgroundAdvice } from "../../lib/backgroundAdvice";
import { Icon } from "../../ui/icons";

/** train_model_dialog.TrainModelPage: dataset check, split, model settings, train. */
export function TrainScreen({ projectId }: { projectId: number }) {
  const [project, setProject] = useState<Project | null>(null);
  const [stats, setStats] = useState<ml.AnnotationStats | null>(null);
  const [statsError, setStatsError] = useState("");
  const [split, setSplit] = useState(80);
  const [size, setSize] = useState("n");
  const [img, setImg] = useState(640);
  const { has, isAdmin, canEdit } = useSession();
  const [setupOpen, setSetupOpen] = useState(false);
  // null: the server sizes the run for the data (enough steps whatever the image count); a number is as typed.
  const [epochs, setEpochs] = useState<number | null>(null);
  const [earlyStop, setEarlyStop] = useState(true);
  const [includeAugmented, setIncludeAugmented] = useState(true);
  const [augmentation, setAugmentation] = useState<ml.AugmentConfig>(DEFAULT_AUGMENT);
  // The image size suggested from the project's images; used unless the person picks one themselves.
  const [suggestion, setSuggestion] = useState<ml.ImageSizeSuggestion | null>(null);
  const imgPicked = useRef(false);
  const [lastRun, setLastRun] = useState<ml.TrainingMetrics | null>(null);
  // CPU or GPU: what the server offers for this size, and the person's pick (null: take the suggestion). The pick is
  // kept with the project (customer feedback, Oliver: "keep the setting in Training on permanent after selection").
  const [options, setOptions] = useState<ml.TrainOptions | null>(null);
  const [picked, setPicked] = useState<ml.TrainDevice | null>(null);
  const [deviceError, setDeviceError] = useState("");
  // Where training starts: "" the latest compatible version (what it always did), "scratch", or a version number.
  const [startFrom, setStartFrom] = useState("");

  const refresh = useCallback(async () => {
    try { setStats(await ml.annotationStats(projectId)); setStatsError(""); }
    catch (e) { setStats(null); setStatsError(`Could not load stats: ${(e as Error).message}`); }
    ml.suggestImageSize(projectId).then((s) => {
      setSuggestion(s);
      if (!imgPicked.current && IMAGE_SIZES.includes(s.suggested)) setImg(s.suggested);
    }).catch(() => {});
  }, [projectId]);

  const job = useTrainingJob(projectId, refresh,
    "Training complete. Download the weights or export it for deployment below, or use Auto-annotate to label the remaining images.");
  const { state: js } = job;
  const model = useModelActions({ id: projectId, name: project?.name ?? "model", deployTarget: project?.deploy_target }, stats?.latest_model_version ?? null, refresh);

  useEffect(() => {
    let alive = true;
    getProject(projectId).then((p) => {
      if (!alive) return;
      setProject(p);
      if (p.train_device) setPicked((cur) => cur ?? p.train_device!);
    }).catch(() => {});
    // Pick up a running job before the first stats read, as load() does; with none running, show the last run's results.
    void job.resume().then((running) => {
      if (!running) ml.trainingMetrics(projectId).then((m) => alive && setLastRun(m)).catch(() => {});
    });
    void refresh();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    let alive = true;
    ml.trainOptions(projectId, size, img).then((o) => alive && setOptions(o)).catch(() => alive && setOptions(null));
    return () => { alive = false; };
  }, [projectId, size, img]);

  const offersGpu = !!options?.gpu_available;
  const pickDevice = (d: ml.TrainDevice) => {
    setPicked(d);
    if (!canEdit) return;
    ml.setTrainDevice(projectId, d).then((p) => { setProject(p); setDeviceError(""); })
      .catch((e) => setDeviceError(`Not kept for next time: ${(e as Error).message}`));
  };
  // A pick the new size rules out (CPU for a run too big for it) falls back to the suggestion.
  const device: ml.TrainDevice | undefined = !offersGpu ? undefined
    : picked === "cpu" && !options!.cpu_fits ? "gpu" : picked ?? options!.recommended;

  const reviewed = stats?.reviewed_annotation_count ?? 0;
  const canTrain = !!stats?.can_train;
  const hasModel = !!stats?.has_trained_model;
  const advice = splitAdvice(split, stats?.reviewed_image_count ?? 0);
  // Versions this run could start from: same model size, image size and architecture options (classes are checked
  // by the server when the run starts).
  // What the model is (corners, outlines, Hailo-shaped) comes from the project setup, never from this page.
  const arch = trainingOptions(project);
  const startable = model.versions.filter((v) => v.available && v.model_size === size && v.img_size === img
    && v.hailo_safe === arch.hailo_safe && v.corners === arch.corners && v.masks === arch.masks);
  const startChoice = startFrom === "scratch" || startable.some((v) => String(v.version_number) === startFrom) ? startFrom : "";
  const params = {
    epochs, train_split: split / 100, model_size: size, img_size: img, ...arch, early_stop: earlyStop, augmentation, include_augmented: includeAugmented,
    ...(device ? { device } : {}),
    ...(startChoice === "scratch" ? { from_scratch: true } : startChoice ? { start_from: Number(startChoice) } : {}),
  };

  const results = summarizeResults(js.metrics ?? lastRun);
  const summary = runSummary(params, device, startChoice === "scratch" ? "scratch" : startChoice ? Number(startChoice) : null, stats?.auto_epochs);
  const augmented = stats?.augmented_reviewed_image_count ?? 0;
  // Augmented copies, when they would help: few images or a rare class before a run, a class scored Needs work after one.
  const augAdvice = stats && canEdit && has("augment_images") && !js.active
    ? augmentAdvice(stats, results?.classes.filter((c) => c.band === "weak").map((c) => c.name)) : null;
  // Background pictures (the station empty, hands, tools), when the project has too few: what stops the model
  // firing on the desk. Shown to anyone who can add pictures, before and after a run.
  // With Augment (Pro) the page can also make them from the project's own pictures, objects erased: the fallback.
  const canMakeBackgrounds = has("augment_images");
  const bgAdvice = stats && canEdit && !js.active ? backgroundAdvice(stats, canMakeBackgrounds) : null;
  const generatedBackgrounds = stats?.generated_background_count ?? 0;

  return (
    <div className="train-page" data-testid="train-page">
      <div className="page-head">
        <div>
          <h1 className="page-title" data-testid="train-title">Train — {project?.name ?? ""}</h1>
          <div className="muted">Teach a model from this project's reviewed annotations. The settings are chosen for you; change them under Advanced if you need to.</div>
        </div>
      </div>

      <div className="train-grid">
        <div className="train-main">
          <section className="card-section" data-testid="train-setup">
            <div className="section-head">
              <h2>What the model learns</h2>
              {isAdmin && project && (
                <button className="btn" onClick={() => setSetupOpen(true)} data-testid="train-setup-change">{isSetUp(project) ? "Change setup…" : "Set up…"}</button>
              )}
            </div>
            <div className="setup-line" data-testid="train-setup-line">{setupLine(project)}</div>
            <div className="muted">
              {isSetUp(project) ? (isAdmin ? "Fixed into every model this project trains. Changing it means training again." : "Set by an admin. It is fixed into every model this project trains.")
                : isAdmin ? "Not set up yet: until you choose, the model finds boxes and is built for a CPU, as before."
                  : "Not set up yet, so the model finds boxes and is built for a CPU. Ask an admin to change this."}
            </div>
          </section>

          <section className="card-section" data-testid="train-data">
            <div className="section-head"><h2>Training data</h2>
              <button className="btn ghost" onClick={refresh} data-testid="train-recheck" title="Read the counts again"><Icon name="refresh" />Refresh</button></div>
            <div className="mini-tiles" data-testid="train-stats">
              <div><span className="mini-value">{stats?.reviewed_annotation_count ?? "–"}</span><span className="muted">reviewed annotations</span></div>
              <div><span className="mini-value">{stats?.reviewed_image_count ?? "–"}</span><span className="muted">images they are on</span></div>
              <div><span className="mini-value">{stats?.unreviewed_annotation_count ?? "–"}</span><span className="muted">not reviewed (left out)</span></div>
              <div><span className="mini-value">{stats ? (stats.latest_model_version ? `v${stats.latest_model_version}` : "none") : "–"}</span><span className="muted">trained model</span></div>
            </div>
            {statsError && <div className="warning-text">{statsError}</div>}
            <div className="muted" style={{ marginTop: 8 }} data-testid="train-split-summary">
              {splitLabel(split, stats?.reviewed_image_count ?? 0)}: the held-back images check how good the model is.
            </div>
            {advice && <div className="warning-text" data-testid="train-split-advice">{advice}</div>}
            {bgAdvice && (
              <div className="warning-text" data-testid="train-background-advice" data-kind={bgAdvice.kind}>
                {bgAdvice.text}{" "}
                <button className="btn flat" onClick={() => navigate({ name: "dashboard" })} data-testid="train-background-gallery">Open the gallery →</button>
                {canMakeBackgrounds && (
                  <button className="btn flat" onClick={() => navigate({ name: "augment", projectId, background: true })} data-testid="train-background-make"
                    title="Copies of your reviewed pictures with the objects erased, saved as Empty. A fallback: real photos of the empty station are better.">
                    Make them from my photos →</button>
                )}
              </div>
            )}
            {augmented > 0 && (
              <div className="row" style={{ marginTop: 8 }} data-testid="train-augmented">
                <label className="check" data-testid="train-include-augmented-label"
                  title={"Copies made on the Augment page, generated background pictures included. They train on the training side only, next to their original, and are never used as validation images. Leave them out to compare a run without them."}>
                  <input type="checkbox" checked={includeAugmented} onChange={(e) => setIncludeAugmented(e.target.checked)} data-testid="train-include-augmented" />
                  Include the {augmented} augmented cop{augmented === 1 ? "y" : "ies"}{generatedBackgrounds > 0 && ` (${generatedBackgrounds} generated background picture${generatedBackgrounds === 1 ? "" : "s"})`}</label>
                <button className="btn flat" onClick={() => navigate({ name: "augment", projectId })} data-testid="train-manage-augmented">Manage →</button>
              </div>
            )}
          </section>

          {results && (
            <section className="card-section" data-testid="train-results">
              <div className="section-head"><h2>How good is it?</h2></div>
              <ValidationResults metrics={js.metrics ?? lastRun} />
            </section>
          )}

          <section className="card-section" data-testid="train-model">
            <div className="section-head"><h2>Your model</h2>
              <button className="btn ghost" onClick={() => navigate({ name: "models", projectId })} data-testid="train-all-models">
                {model.versions.length > 1 ? `All ${model.versions.length} versions →` : "Models →"}</button></div>
            {hasModel ? <div className="muted" style={{ marginBottom: 8 }} data-testid="train-model-line">
                Model v{stats!.latest_model_version} is the latest. Download its weights, or export it for the device it will run on. Earlier versions are on the Models page.</div>
              : <div className="muted" style={{ marginBottom: 8 }}>No model yet. Train one, or import weights on the Models page.</div>}
            <div className="row">
              <button className="btn" disabled={!hasModel || model.saving} onClick={model.save} data-testid="train-save">
                {model.saving ? "Downloading…" : <><Icon name="download" />Download weights (.pt)</>}</button>
              <button className="btn" disabled={!hasModel || model.converting} onClick={model.openOnnx} data-testid="train-onnx"
                title="Package the trained model for the device it will run on: ctrlX, an NVIDIA GPU or a Hailo accelerator.">
                {model.converting ? "Exporting…" : <><Icon name="cube" />Export for deployment…</>}</button>
            </div>
          </section>

          <details className="advanced card-section" data-testid="train-advanced">
            <summary>Advanced</summary>
            <div className="advanced-grid">
              <div className="field">
                <label htmlFor="train-split">Training / validation split</label>
                <div className="row" style={{ flexWrap: "nowrap" }}>
                  <input id="train-split" type="range" min={50} max={95} step={5} value={split} style={{ flex: 1 }} onChange={(e) => setSplit(Number(e.target.value))} data-testid="train-split" />
                  <span style={{ whiteSpace: "nowrap" }} data-testid="train-split-label">{splitLabel(split, stats?.reviewed_image_count ?? 0)}</span>
                </div>
                <SplitGuide />
              </div>
              <div className="field">
                <label htmlFor="train-size">Model size</label>
                <select id="train-size" className="select" value={size} onChange={(e) => setSize(e.target.value)} data-testid="train-size">{MODEL_SIZES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              </div>
              <div className="field">
                <label htmlFor="train-img">Image size</label>
                <select id="train-img" className="select" value={img} onChange={(e) => { imgPicked.current = true; setImg(Number(e.target.value)); }} data-testid="train-img">{IMAGE_SIZES.map((v) => <option key={v} value={v}>{v} × {v}{suggestion?.suggested === v ? "  (suggested)" : ""}</option>)}</select>
                {suggestion && (
                  <div className="muted" data-testid="train-img-suggestion">
                    Suggested from your images: <b>{suggestion.suggested}</b>. {suggestion.reason}
                    {img !== suggestion.suggested && <> <button className="btn flat" onClick={() => setImg(suggestion.suggested)} data-testid="train-img-use">Use {suggestion.suggested}</button></>}
                  </div>
                )}
              </div>
              <div className="field">
                <label htmlFor="train-epochs">Epochs (passes over the data)</label>
                <label className="check" data-testid="train-epochs-auto-label"
                  title={"A model needs a certain number of learning steps however many images it has, so few images need more passes than many. With this on, the number is worked out from your images."}>
                  <input type="checkbox" checked={epochs === null} data-testid="train-epochs-auto"
                    onChange={(e) => setEpochs(e.target.checked ? null : (stats?.auto_epochs ?? 60))} />
                  Choose for my images (recommended){epochs === null && stats?.auto_epochs ? ` — about ${stats.auto_epochs}` : ""}</label>
                <input id="train-epochs" className="input" type="number" min={10} max={300} value={epochs ?? stats?.auto_epochs ?? 60} style={{ width: 120 }} data-testid="train-epochs"
                  disabled={epochs === null}
                  onChange={(e) => setEpochs(Math.min(300, Math.max(10, Math.trunc(Number(e.target.value)) || 10)))} />
                <label className="check" data-testid="train-early-label"
                  title={"Validation runs every 5 epochs. With this on, training stops once several validations in a row have not beaten the best score, and never in the first half of the run.\n\nThe epochs above become a maximum. The saved model is the best epoch either way."}>
                  <input type="checkbox" checked={earlyStop} onChange={(e) => setEarlyStop(e.target.checked)} data-testid="train-early-stop" />Stop early when the score stops improving</label>
              </div>
              <div className="field">
                <label htmlFor="train-start">Start from</label>
                <select id="train-start" className="select" value={startChoice} onChange={(e) => setStartFrom(e.target.value)} data-testid="train-start"
                  title={"Fine-tuning continues an earlier version's training, so it needs fewer epochs. Only versions with this model size, image size and setup are listed."}>
                  <option value="">Latest compatible version (default)</option>
                  <option value="scratch">From scratch</option>
                  {startable.map((v) => <option key={v.version_number} value={String(v.version_number)}>{versionLabel(v)}</option>)}
                </select>
              </div>
              {offersGpu && options && (
                <div className="field" data-testid="train-device">
                  <label>Train on</label>
                  <div className="row">
                    <label className="check" title={options.cpu_note ?? "The CPU server. Slower for large models and image sizes."}>
                      <input type="radio" name="device" checked={device === "cpu"} disabled={!options.cpu_fits || js.active} onChange={() => pickDevice("cpu")} data-testid="train-device-cpu" />
                      CPU{options.recommended === "cpu" ? "  (recommended)" : ""}{options.cpu_fits ? "" : "  —  too big for the CPU server"}</label>
                    <label className="check" title="A GPU machine. Many times faster for large models and image sizes.">
                      <input type="radio" name="device" checked={device === "gpu"} disabled={js.active} onChange={() => pickDevice("gpu")} data-testid="train-device-gpu" />
                      GPU{options.recommended === "gpu" ? "  (recommended)" : ""}</label>
                  </div>
                  <div className="muted" data-testid="train-device-reason">{options.reason}</div>
                  {project?.train_device && !deviceError && (
                    <div className="muted" data-testid="train-device-kept">{project.train_device.toUpperCase()} is kept for this project, for everyone who trains it
                      {device === project.train_device ? "." : "; this run is too big for it, so it goes to the GPU."}</div>
                  )}
                  {deviceError && <div className="warning-text" data-testid="train-device-error">{deviceError}</div>}
                </div>
              )}
              <div className="field"><AugmentPanel projectId={projectId} value={augmentation} onChange={setAugmentation} /></div>
            </div>
            <SettingsGuide />
          </details>
        </div>

        <aside className="train-run" data-testid="train-run">
          <div className="run-card">
            <div className="tile-label">Ready to train?</div>
            {canTrain ? (
              <div className="run-ready" data-testid="train-check"><Icon name="check" size={18} />Ready: {reviewed} reviewed annotations</div>
            ) : (
              <div className="run-notready" data-testid="train-check">
                {stats ? `Review ${MIN_REVIEWED - reviewed} more annotation${MIN_REVIEWED - reviewed === 1 ? "" : "s"} to train (${MIN_REVIEWED} needed).` : statsError ? "Could not check the data." : "Checking…"}
              </div>
            )}
            <div className="tile-bar" data-testid="train-progress" aria-label={canTrain ? `${reviewed} reviewed` : `${reviewed} / ${MIN_REVIEWED} reviewed`}>
              <i style={{ width: `${(Math.min(reviewed, MIN_REVIEWED) / MIN_REVIEWED) * 100}%` }} />
            </div>
            {!canTrain && stats && (
              <button className="btn flat" style={{ alignSelf: "flex-start", padding: 0 }} onClick={() => navigate({ name: "dashboard" })} data-testid="train-go-annotate">Go to the images →</button>
            )}
            {augAdvice && augAdvice.kind !== "weak" && <AugmentAdvice advice={augAdvice} projectId={projectId} />}
            <div className="run-summary" data-testid="train-summary">{summary}</div>
            <div className="row" style={{ flexWrap: "nowrap" }}>
              <button className="btn primary run-go" disabled={!canTrain || !canEdit || js.active || js.submitting} data-testid="train-btn"
                onClick={() => job.start(params, `${configLine(params)}${device ? `, device=${device}` : ""}${startChoice === "scratch" ? ", from scratch" : startChoice ? `, from v${startChoice}` : ""}`)}>
                {js.submitting ? "Submitting…" : js.active ? "Training…" : <><Icon name="play" />Train</>}</button>
              {js.active && <button className="btn danger" disabled={js.stopping} onClick={job.stop} data-testid="train-stop">{js.stopping ? "Stopping…" : <><Icon name="stop" />Stop training</>}</button>}
            </div>
            {js.statusText && <div className="run-status" data-testid="train-status">{js.statusText}</div>}
            <JobLog log={js.log} metrics={js.metrics} testId="train-log" collapsible />
            {results && !js.active && (
              <div className={`run-verdict ${results.band}`} data-testid="train-verdict">
                <b>{results.verdict}</b>
                {(results.band === "good" || results.band === "usable") && (stats?.unannotated ?? 0) > 0 && (
                  <button className="btn flat" style={{ padding: 0 }} onClick={() => navigate({ name: "auto", projectId })} data-testid="train-next-auto">
                    Auto-annotate the {stats!.unannotated} remaining image{stats!.unannotated === 1 ? "" : "s"} →</button>
                )}
              </div>
            )}
            {augAdvice?.kind === "weak" && <AugmentAdvice advice={augAdvice} projectId={projectId} />}
          </div>
        </aside>
      </div>

      {model.panel}
      {setupOpen && project && (
        <ProjectSetupDialog project={project} hasModels={hasModel} has={has} onClose={() => setSetupOpen(false)}
          onSaved={(p) => { setProject(p); setSetupOpen(false); projectsChanged(); projectDataChanged(); }} />
      )}
    </div>
  );
}

/** A suggestion to make augmented copies, with the way to the Augment page (set to the class, when it is about one). */
function AugmentAdvice({ advice, projectId }: { advice: Advice; projectId: number }) {
  return (
    <div className="run-advice" data-testid="train-augment-advice">
      <span>{advice.text}</span>
      <button className="btn flat" style={{ alignSelf: "flex-start", padding: 0 }} onClick={() => navigate({ name: "augment", projectId, classId: advice.classId })}
        data-testid="train-augment-go">{advice.classId ? `Augment the ${advice.className} images →` : "Make augmented copies →"}</button>
    </div>
  );
}
